import React from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Icon } from '@iconify/react';
import Button from '@mui/material/Button';

/**
 * AdminProtectedRoute Component
 * Wraps routes that require ADMIN role
 * Shows 403 Forbidden page if not admin
 */
export const AdminProtectedRoute = ({ children }) => {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // If not logged in at all, redirect to login
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // If logged in but not admin, show 403 Forbidden page
  if (user.role !== 'ADMIN') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white shadow-md rounded-lg p-6 text-center">
          {/* 4🔒3 Error Code */}
          <h1 className="text-6xl font-bold text-gray-800 mb-3">
            4<span role="img" aria-label="lock">🔒</span>3
          </h1>

          {/* Title */}
          <h2 className="text-xl font-semibold text-gray-800 mb-2">
            Restricted Access
          </h2>

          {/* Message */}
          <p className="text-gray-600 text-sm mb-4">
            You lack permissions to access this page.
          </p>

          {/* Back Button */}
          <Button
            variant="contained"
            onClick={() => navigate(-1)}
            startIcon={<Icon icon="tabler:arrow-left" width={16} />}
            sx={{
              backgroundColor: '#2563EB',
              textTransform: 'none',
              fontSize: '0.875rem',
              fontWeight: 500,
              '&:hover': { backgroundColor: '#1D4ED8' },
            }}
          >
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  // User is admin, render children
  return children;
};

export default AdminProtectedRoute;
